import { Loader2 } from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import { useIdentityVerificationStatus } from '@/hooks/useIdentityVerificationStatus';
import {
  continueAffordanceForStatus,
  deriveIdentityVerificationView,
  messageForKycError,
} from '@/lib/identity-verification-view';

/**
 * Dedicated "Verifica identità" step content (PR-DIDIT-6B). Drives start /
 * resume / retry of the KYC session and offers "Continua ai documenti" once
 * the verification is done / under review / unavailable (honest-mode). The page
 * shell provides the title + intro copy; this renders the status + actions.
 *
 * KYC stays advisory here: the upload is never technically blocked — the
 * conversion gate lives in the backoffice (DIDIT-7/-10).
 */
export interface IdentityVerificationStepProps {
  uploadToken: string;
  /** Navigate to the documents page (state preserved by the caller). */
  onContinue: () => void;
  /** BE rejected the token (403) — drop the page (session expired). */
  onSessionExpired: () => void;
}

export function IdentityVerificationStep({
  uploadToken,
  onContinue,
  onSessionExpired,
}: IdentityVerificationStepProps): JSX.Element {
  const { phase, data, actionError, starting, refresh, start } =
    useIdentityVerificationStatus(uploadToken, { onSessionExpired });

  if (phase === 'loading' && data === null) {
    return (
      <div className="grid gap-2">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Verifica identità…
        </span>
      </div>
    );
  }

  const requiresKyc = data?.requiresKyc ?? true;

  // KYC not required for this case type → nothing to verify; let the candidate
  // proceed straight to the documents.
  if (!requiresKyc) {
    return (
      <div className="grid gap-4">
        <p className="text-sm text-muted-foreground">
          Per questo profilo non è richiesta la verifica identità. Puoi
          proseguire con i documenti.
        </p>
        <div className="pt-1">
          <PillButton type="button" onClick={onContinue}>
            Continua ai documenti
          </PillButton>
        </div>
      </div>
    );
  }

  const status = data?.status ?? 'PROVIDER_ERROR';
  const view = deriveIdentityVerificationView(status);
  const affordance = continueAffordanceForStatus(status);
  // Honest-mode escape: a failed "Avvia" click leaves the status NOT_STARTED but
  // surfaces PROVIDER_UNAVAILABLE — still offer a soft "Continua ai documenti".
  const showContinue =
    affordance !== 'none' || actionError === 'PROVIDER_UNAVAILABLE';
  const continueIsPrimary = affordance === 'primary';

  return (
    <div className="grid gap-4">
      <div>
        <SoftBadge tone={view.tone}>{view.badgeLabel}</SoftBadge>
      </div>
      <p className="text-sm text-muted-foreground">{view.description}</p>
      {actionError ? (
        <p className="text-xs text-state-danger">
          {messageForKycError(actionError)}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {view.cta ? (
          <PillButton
            type="button"
            disabled={starting}
            onClick={() => {
              void start();
            }}
          >
            {starting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : null}
            {view.cta.label}
          </PillButton>
        ) : null}
        {view.showRefresh ? (
          <PillButton
            type="button"
            variant="ghost"
            disabled={starting}
            onClick={() => {
              void refresh();
            }}
          >
            Aggiorna stato
          </PillButton>
        ) : null}
        {showContinue ? (
          <PillButton
            type="button"
            variant={continueIsPrimary ? 'primary' : 'ghost'}
            onClick={onContinue}
          >
            Continua ai documenti
          </PillButton>
        ) : null}
      </div>
    </div>
  );
}
